import React, { useContext, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import { CustomersContext } from "../../context/customersContext";
import { Button } from "../ui";

const BLANK = {
    contractor: "Yes",
    company: "",
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    address: "",
    city: "",
    province: "ON",
    postalCode: "",
    notes: [],
    recentActivities: [],
};

const label = {
    display: "block",
    marginBottom: "0.3rem",
    fontSize: "0.78rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: "#64748b",
};

const inputBase = {
    width: "100%",
    padding: "0.5rem 0.65rem",
    fontSize: "0.93rem",
    color: "#1e293b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
};

const focusHandlers = {
    onFocus: (e) => {
        e.target.style.borderColor = "#2c7aa0";
        e.target.style.boxShadow = "0 0 0 3px rgba(44,122,160,0.12)";
        e.target.style.background = "#ffffff";
    },
    onBlur: (e) => {
        e.target.style.borderColor = "#e2e8f0";
        e.target.style.boxShadow = "none";
        e.target.style.background = "#f8fafc";
    },
};

const field = {
    marginBottom: "1rem",
};

const row = {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "1rem",
};

const FormCustomer = ({ setShowModal, onSuccess }) => {
    const { action } = useContext(CustomersContext);
    const [customer, setCustomer] = useState(BLANK);
    const [errors, setErrors] = useState([]);
    const [added, setAdded] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = (name, value) => setCustomer((prev) => ({ ...prev, [name]: value }));
    const handleChange = (e) => set(e.target.name, e.target.value);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = [];
        if (!customer.firstName.trim()) errs.push("firstName");
        if (!customer.mobile.trim()) errs.push("mobile");
        if (errs.length) {
            setErrors(errs);
            return;
        }

        setSaving(true);
        try {
            await action.addCustomer(customer);
            setCustomer(BLANK);
            setErrors([]);
            setAdded(true);
        } catch (err) {
            console.error("Error adding customer:", err);
        } finally {
            setSaving(false);
        }
    };

    const inputField = (labelText, name, value, required) => (
        <div style={field}>
            <label
                style={{
                    ...label,
                    color: required && errors.includes(name) ? "#dc2626" : "#64748b",
                }}
            >
                {labelText}
                {required && " *"}
            </label>
            <input
                style={{
                    ...inputBase,
                    borderColor: required && errors.includes(name) ? "#fca5a5" : "#e2e8f0",
                }}
                type="text"
                name={name}
                value={value}
                onChange={handleChange}
                placeholder={labelText}
                {...focusHandlers}
            />
            {required && errors.includes(name) && (
                <p style={{ margin: "0.25rem 0 0", fontSize: "0.78rem", color: "#dc2626" }}>
                    Required
                </p>
            )}
        </div>
    );

    if (added) {
        return (
            <div style={{ textAlign: "center", padding: "2rem 0" }}>
                <i
                    className="fas fa-check-circle"
                    style={{
                        fontSize: "3rem",
                        color: "#16a34a",
                        marginBottom: "1rem",
                        display: "block",
                    }}
                />
                <p style={{ fontWeight: 700, color: "#15803d", marginBottom: "1.5rem" }}>
                    Customer successfully added!
                </p>
                <div style={{ display: "flex", justifyContent: "center", gap: "0.75rem" }}>
                    <Button variant="secondary" size="sm" onClick={() => setAdded(false)}>
                        Add Another
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setShowModal(false)}>
                        Close
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form id="customer-form" onSubmit={handleSubmit}>
            {errors.length > 0 && (
                <p
                    style={{
                        color: "#dc2626",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        marginBottom: "1rem",
                    }}
                >
                    Please fill out the required fields.
                </p>
            )}

            <div style={{ ...row, gridTemplateColumns: "1fr 2fr" }}>
                <div style={field}>
                    <label style={label}>Contractor</label>
                    <select
                        style={inputBase}
                        name="contractor"
                        value={customer.contractor}
                        onChange={handleChange}
                        {...focusHandlers}
                    >
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                    </select>
                </div>
                {inputField("Company", "company", customer.company)}
            </div>

            <div style={row}>
                {inputField("First Name", "firstName", customer.firstName, true)}
                {inputField("Last Name", "lastName", customer.lastName)}
            </div>

            <div style={row}>
                <div style={field}>
                    <label
                        style={{
                            ...label,
                            color: errors.includes("mobile") ? "#dc2626" : "#64748b",
                        }}
                    >
                        Mobile *
                    </label>
                    <PhoneInput
                        inputStyle={{
                            ...inputBase,
                            borderColor: errors.includes("mobile") ? "#fca5a5" : "#e2e8f0",
                        }}
                        buttonStyle={{ display: "none" }}
                        country="ca"
                        disableDropdown
                        disableCountryGuess
                        disableCountryCode
                        value={customer.mobile}
                        onChange={(value) => set("mobile", value)}
                        placeholder="Phone number"
                    />
                    {errors.includes("mobile") && (
                        <p style={{ margin: "0.25rem 0 0", fontSize: "0.78rem", color: "#dc2626" }}>
                            Required
                        </p>
                    )}
                </div>
                {inputField("Email", "email", customer.email)}
            </div>

            {inputField("Address", "address", customer.address)}

            <div style={{ ...row, gridTemplateColumns: "2fr 1fr 1fr" }}>
                {inputField("City", "city", customer.city)}

                <div style={field}>
                    <label style={label}>Province</label>
                    <select
                        style={inputBase}
                        name="province"
                        value={customer.province}
                        onChange={handleChange}
                        {...focusHandlers}
                    >
                        {[
                            "ON",
                            "AB",
                            "BC",
                            "MB",
                            "NB",
                            "NL",
                            "NT",
                            "NS",
                            "NU",
                            "PE",
                            "QC",
                            "SK",
                            "YT",
                        ].map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </select>
                </div>

                {inputField("Postal Code", "postalCode", customer.postalCode)}
            </div>
        </form>
    );
};

export default FormCustomer;
